/**
 * Computes the Current Week Summary (Mon–Sun) from production records.
 *
 * Only the LATEST production record for each calendar day is used.
 * Multiple Firestore production docs can exist for the same date when a
 * record is "saved over" by creating a new doc instead of updating the
 * existing one. Using all of them would incorrectly sum historical totals.
 *
 * "Latest" is determined by the Firestore document ID (auto-generated IDs
 * are lexicographically ordered by creation time). When IDs are absent or
 * identical, the document appearing later in the array wins.
 *
 * Within the selected document, pieces are still aggregated across all lines
 * and production lists (this is correct — multiple lines on the same day
 * belong to the same production run).
 *
 * @param {Object} params
 * @param {Array}  params.productions  - Array of production Firestore docs.
 * @param {Array}  params.products     - Array of product Firestore docs.
 * @param {Array}  params.weekDates    - Array of 7 Date objects (Mon..Sun).
 * @param {Function} params.toISODate  - (Date) => "YYYY-MM-DD" string.
 * @param {Function} params.parseISODateLocal - (iso) => local Date.
 * @param {Array}  params.doughs       - Array of dough Firestore docs (for mix info).
 * @param {Array}  params.lines        - Array of line Firestore docs (for production time).
 * @returns {Object} Map of "YYYY-MM-DD" → sorted array of summary rows.
 */
export function computeWeekSummary({ productions, products, weekDates, toISODate, parseISODateLocal, doughs = [], lines = [] }) {
  // --- Step 1: For each date, select only the LATEST production document. ---
  // Firestore auto-generated IDs are lexicographically ordered by creation
  // time, so a string comparison reliably identifies the newest document.
  // When IDs are equal or absent, the later entry in the array wins.
  const latestByDate = {};
  productions.forEach((prod) => {
    if (!prod.date) return;
    const prodKey = toISODate(parseISODateLocal(prod.date));
    const existing = latestByDate[prodKey];
    if (!existing) {
      latestByDate[prodKey] = prod;
    } else {
      const existingId = String(existing.id || "").split("/").pop();
      const prodId = String(prod.id || "").split("/").pop();
      if (prodId >= existingId) {
        latestByDate[prodKey] = prod;
      }
    }
  });

  // --- Step 2: Build per-day accumulators from the latest doc only. ---
  // Initialise one accumulator map per day in the current week.
  // Each accumulator is keyed by productId and holds running totals.
  const dayAccumulators = {};
  weekDates.forEach((d) => {
    dayAccumulators[toISODate(d)] = {};
  });

  weekDates.forEach((d) => {
    const prodKey = toISODate(d);
    const prod = latestByDate[prodKey];
    if (!prod) return;

    Object.entries(prod.lineProductQuantities || {}).forEach(([, lineMap]) => {
      Object.entries(lineMap).forEach(([lineId, productMap]) => {
        Object.entries(productMap).forEach(([productId, info]) => {
          const product = products.find((p) => p.id === productId);
          if (!product) return;

          const qty = Number(info.quantity) || 0;
          const unitPieces = info.unit?.pieceCount
            ? parseInt(info.unit.pieceCount, 10)
            : 1;
          const pieces = qty * unitPieces;

          const acc = dayAccumulators[prodKey];
          if (!acc[productId]) {
            // First time we see this product for this day – seed the entry.
            acc[productId] = {
              name: product.name || productId,
              product,
              unitPieces,
              unitName: info.unit?.name || "piece",
              totalPieces: 0,
              perLinePieces: {},
            };
          }
          acc[productId].totalPieces += pieces;
          // Track per-line piece counts for production time calculation.
          acc[productId].perLinePieces[lineId] =
            (acc[productId].perLinePieces[lineId] || 0) + pieces;
        });
      });
    });
  });

  // Convert each day's accumulator map into a sorted display array.
  const summary = {};
  Object.entries(dayAccumulators).forEach(([dateKey, acc]) => {
    summary[dateKey] = Object.values(acc)
      .map(({ name, product, unitPieces, unitName, totalPieces, perLinePieces }) => {
        // Derive displayable quantity from aggregated pieces.
        const quantity =
          unitPieces > 0 ? Math.round(totalPieces / unitPieces) : totalPieces;

        let racksNeeded = null;
        let boardsNeeded = null;
        if (product.boardPerRack && product.piecePerBoard) {
          const boardsPerRack = parseInt(product.boardPerRack, 10);
          const piecesPerBoard = parseInt(product.piecePerBoard, 10);
          if (boardsPerRack > 0 && piecesPerBoard > 0) {
            racksNeeded = Math.ceil(
              totalPieces / (piecesPerBoard * boardsPerRack)
            );
            boardsNeeded = Math.ceil(totalPieces / piecesPerBoard);
          }
        }

        // --- Mixes ---
        // Find the first dough ingredient on this product to derive mix count
        // and mix weight (maxMix on the dough document).
        let mixCount = null;
        let mixWeightGrams = null;
        const doughIngredient = (product.ingredients || []).find(
          (ing) => String(Object.keys(ing)[0]).startsWith("doughs/")
        );
        if (doughIngredient) {
          const doughRef = Object.keys(doughIngredient)[0];
          const ingredientAmountPerPiece =
            parseFloat(Object.values(doughIngredient)[0]) || 0;
          const dough = doughs.find((d) => d.id === doughRef);
          if (dough) {
            const maxMix = parseFloat(dough.maxMix) || 0;
            if (maxMix > 0 && ingredientAmountPerPiece > 0) {
              const totalWeight = ingredientAmountPerPiece * totalPieces;
              mixCount = Math.ceil(totalWeight / maxMix);
              mixWeightGrams = maxMix;
            }
          }
        }

        // --- Production time ---
        // Sum the run time across each line this product appeared on.
        // run time per line = ceil(piecesOnLine / piecesPerHour) minutes.
        let productionMinutes = null;
        let anyLineFound = false;
        let totalProdMinutes = 0;
        const productIdPlain = String(product.id || "").split("/").pop();
        Object.entries(perLinePieces).forEach(([lineId, piecesOnLine]) => {
          const lineIdStr = String(lineId);
          const lineIdPlain = lineIdStr.split("/").pop();
          const line = lines.find(
            (l) =>
              l.id === lineIdStr ||
              l.id === `lines/${lineIdPlain}` ||
              l.id.split("/").pop() === lineIdPlain
          );
          if (!line) return;

          // line.products is an array of { [productRef]: { piecesPerHour, ... } }
          const lineProductEntry = (line.products || []).find(
            (p) =>
              p[product.id] ||
              p[`products/${productIdPlain}`] ||
              p[productIdPlain]
          );
          const configKey = lineProductEntry
            ? Object.keys(lineProductEntry)[0]
            : null;
          const piecesPerHour =
            parseFloat(lineProductEntry?.[configKey]?.piecesPerHour || 0) || 0;
          if (piecesPerHour > 0) {
            totalProdMinutes += Math.ceil((piecesOnLine / piecesPerHour) * 60);
            anyLineFound = true;
          }
        });
        if (anyLineFound) {
          productionMinutes = totalProdMinutes;
        }

        return {
          name,
          quantity,
          unitName,
          pieces: totalPieces,
          racksNeeded,
          boardsNeeded,
          mixCount,
          mixWeightGrams,
          productionMinutes,
        };
      })
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  });

  return summary;
}
